// P1r (R reports) slice 5 — the "Reports" report pages (Summary / 558).
//
// A report page mirrors the PSU (KIL) page's shape but for a client REPORT
// (report_id ∈ {"summary","558"}) rather than the Project Status Update:
//   - left rail: Generate button + the generations archive (files[] links)
//   - main: the Items PLACED in this report (report_tags ∋ report_id),
//     grouped by Category, each with an interactive "remove from report"
//     toggle (the PSU +/✓ parity — placement onto a report happens on the
//     briefing Item pencil, slice 4; un-placement happens here).
//
// Read source (build-time choice, per the gate doc): the placed rows are
// FILTERED FROM THE LOADED BRIEFING PAYLOAD (report_tags is stamped onto the
// blob from state by P0), NOT a dedicated /report-marks snapshot. That means a
// placement made via THIS page's toggle only re-appears/-disappears on reload
// after the next render re-stamps the blob; the toggle updates the row
// optimistically in the meantime. A dedicated report-marks snapshot (Worker-
// mutated like /kil-marks) is a deliberate follow-up, not v1.
//
// Delivery reuses the Worker's id-keyed report-generations archive (slice 1):
// POST /report-generations {report_id, filename, after} (409→retry while the
// agent re-renders), GET /report-generations (list), and
// /report-generations/:id/download?file=<n> (one file per download link).

import { ApiError, downloadFile, getJson, postJson, wireDownloadControl } from "./api";
import {
  availableLenses,
  compareDisplayNumber,
  fetchBriefing,
  fetchBriefingIndex,
  humanizeSlug,
  projectAssociationSlugs,
  type AnyItem,
  type BriefingPayload,
  type ThreadView,
} from "./briefings";
import { inFixtureMode } from "./fixture";
import { showUndoToast } from "./undo_toast";
import { actionItemKey } from "./target_id";

// The report vocabulary. Matches the agent-side report_id, the
// `report_tags` set members, and the Worker's report_id. Keep in sync with the
// item_set_report_tags multiselect options (forms.ts) + the Pydantic Literal.
export const REPORT_IDS = ["summary", "558"] as const;
export type ReportId = (typeof REPORT_IDS)[number];

// P5c generation modes (cross-repo contract with the Worker mode filter +
// agent/report_axis.py). "combined" = one file, association-major → sub-project;
// the two splits = one file per axis value + a download-all zip.
export type ReportMode = "combined" | "split_association" | "split_subproject";
export const REPORT_MODE_LABELS: Record<ReportMode, string> = {
  combined: "Combined (one file)",
  split_association: "Split by association",
  split_subproject: "Split by sub-project",
};

// Client-facing labels. The internal slug stays "558"; the recipient sees
// "Key Issue Log" (2026-09-09 rename, Ray's own name for the report). `summary`
// is DE-LISTED from the UI (2026-09-09) but keeps its label + renderer dormant.
export const REPORT_LABELS: Record<ReportId, string> = {
  summary: "Summary Report",
  "558": "Key Issue Log",
};

// Heading shown at the top of a report page's main column.
const REPORT_HEADINGS: Record<ReportId, string> = {
  summary: "Scale Summary Report",
  "558": "Key Issue Log",
};

// Which reports have a LIVE backend document renderer (parity with the agent
// registry `_render_report_doc` in agent/cli.py — `summary` renders via P5a's
// scale_summary_docx, `558` via P5a-558's report_558_xlsx; any other report_id
// raises "unknown report"). Generate is gated on this: a report with no renderer
// would otherwise dead-end (POST → 409 regenerating → sync-report raises → the
// SPA retry-loops forever). ⚠️ Keep this in parity with `_render_report_doc`:
// flip a report → true IN THE SAME CHANGE that adds its cli.py branch, or the
// gate lies.
export const REPORT_HAS_RENDERER: Record<ReportId, boolean> = {
  summary: true,
  "558": true,
};

export function isReportId(v: string): v is ReportId {
  return (REPORT_IDS as readonly string[]).includes(v);
}

// --- Delivery: report-generations archive (Worker slice 1) ------------------

/** One downloadable file inside a report generation (b64 stripped in the
 *  list response — the SPA fetches bytes only on Download). */
export interface ReportGenerationFile {
  report: string;
  axis: string | null;
  format: string;
  filename: string;
}

export interface ReportGenerationListItem {
  id: string;
  report_id: string;
  generated_at: string;
  generated_by: string;
  week_end: string | null;
  filename: string;
  label: string | null | undefined;
  files: ReportGenerationFile[];
}

export interface ReportGenerationsResponse {
  project_slug: string;
  generations: ReportGenerationListItem[];
}

export async function fetchReportGenerations(): Promise<ReportGenerationsResponse> {
  return getJson<ReportGenerationsResponse>("/report-generations");
}

export interface ReportGenerationCreateResponse {
  id: string;
  generated_at: string;
  report_id: string;
  filename: string;
  label: string | null | undefined;
  files: number;
}

/** Result of a Generate click: `created` on a 201, or `regenerating` when the
 *  Worker kicked a fresh render + 409'd (missing / stale staging blob). The
 *  caller shows the regenerating note and lets the recipient retry. */
export type ReportGenerationOutcome =
  | { status: "created"; result: ReportGenerationCreateResponse }
  | { status: "regenerating"; detail: string };

export async function createReportGeneration(input: {
  report_id: ReportId;
  filename: string;
  label: string | null;
  /** P5c: combined / split-by-association / split-by-sub-project. Omitted on a
   *  flat project → the Worker carries the single staged file (back-compat). */
  mode?: ReportMode | null;
}): Promise<ReportGenerationOutcome> {
  // Fixture mode has no Worker — return a synthetic success so the preview
  // still exercises the form → archive-refresh flow without persisting.
  if (inFixtureMode()) {
    return {
      status: "created",
      result: {
        id: crypto.randomUUID(),
        generated_at: new Date().toISOString(),
        report_id: input.report_id,
        filename: input.filename,
        label: input.label,
        files: 1,
      },
    };
  }
  try {
    const result = await postJson<ReportGenerationCreateResponse>(
      "/report-generations",
      {
        report_id: input.report_id,
        filename: input.filename,
        label: input.label,
        ...(input.mode ? { mode: input.mode } : {}),
      },
    );
    return { status: "created", result };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      const body = (e.body as { error?: string; detail?: string } | null) ?? {};
      if (body.error === "regenerating") {
        return {
          status: "regenerating",
          detail:
            body.detail
            ?? "The report is being generated from your latest placements. "
              + "Click Generate again shortly.",
        };
      }
    }
    throw e;
  }
}

export function reportGenerationDownloadUrl(id: string, fileIndex: number): string {
  // Resolved against the same Worker origin the SPA already uses; the api.ts
  // wrapper prepends WORKER_URL. `?file=<n>` selects one file from files[].
  return `/report-generations/${encodeURIComponent(id)}/download?file=${fileIndex}`;
}

// --- Placement: item_set_report_tags (replace-whole) ------------------------

async function latestRunId(): Promise<string | null> {
  const list = await fetchBriefingIndex();
  return list[0]?.run_id ?? null;
}

/** The replace-whole set to send when UN-placing an Item from `reportId`:
 *  the row's current set with THIS report removed, other placements preserved,
 *  deduped. The wire kind is replace-whole, so sending the reduced full set
 *  (never a delta) is what keeps a row placed in both reports in the other one.
 *  This is the footgun the slice-4 pencil guards with pre-checking; the on-page
 *  toggle guards it here. */
export function computeUnplacedTags(current: string[], reportId: ReportId): string[] {
  const out: string[] = [];
  for (const t of current) {
    if (t === reportId) continue;
    if (!out.includes(t)) out.push(t);
  }
  return out;
}

/** Set the COMPLETE report_tags set on a placed ACTION (replace-whole — the wire
 *  kind carries the full desired set, NOT a delta). The Key Issue Log was re-keyed
 *  Item→Action (Slice 1, 2026-09-09), so un-placement here posts against the ACTION
 *  target (`action_set_report_tags`). The caller computes the new set by removing
 *  this page's report_id from the action's current set, so an action placed in
 *  multiple reports keeps its other placements on un-place. `runId` anchors the
 *  correction event (the caller already fetched it to walk placed rows). */
export async function setActionReportTags(
  runId: string,
  actionTargetId: string,
  newTags: string[],
): Promise<void> {
  if (inFixtureMode()) return; // no Worker — the caller updates the row optimistically
  await postJson(`/feedback/${encodeURIComponent(runId)}`, {
    kind: "action_set_report_tags",
    target: { type: "action_item", id: actionTargetId },
    payload: { report_tags: newTags },
  });
}

// --- Placed-actions walk over the briefing payload --------------------------

// Action statuses that never render on a report page (parity with
// portal.kil_action_retained): acknowledged / not-relevant drop at once. A
// `closed` action follows the PSU's 1-week rule — the briefing payload strips
// closed actions at render, so recently-closed rows come from the kil-marks
// snapshot (`key_issue_log.closed_actions`, merged in loadReportViewData); a
// closed action still in the payload (an in-session close) renders as closed.
const DROPPED_ACTION_STATUS = new Set(["acknowledged", "not_relevant"]);

/** A briefing ACTION placed in a given report, with the parent Item + Category
 *  context needed to group it and the target id needed to un-place it. The
 *  association / sub-project / sub-category / point fields (T1 report-parity,
 *  2026-10-28) carry the grouping axes the Key Issue Log report page regroups by,
 *  mirroring the generated .xlsx (agent/report_558_xlsx.py). */
export interface PlacedReportAction {
  threadSlug: string | null;
  threadName: string;
  threadDisplayNumber: string | null;
  itemSlug: string;
  itemName: string;
  itemDisplayNumber: string | null;
  action: AnyItem;
  /** The action's current report_tags (the replace-whole base for un-place). */
  reportTags: string[];
  /** `thread:<t>/work_item:<wi>/action_item:<ai>` — null for an unthreaded or
   *  ai_slug-less action (display-only; the remove control is then suppressed). */
  targetId: string | null;
  /** The association slugs this action bands under (F-FLAT, ultra #520): each action
   *  bands by its OWN `association_tags` (a both-tower action appears in each band);
   *  a merged-closed row uses the closed action's own tags. */
  associations: string[];
  /** The 558 "Point" (responsible firm/s) off the parent Item (`point`), echoed
   *  onto each action row like the .xlsx. "" when unset. */
  point: string;
}

function walkThreadActions(
  thread: ThreadView | undefined,
  slug: string | null,
  name: string,
  displayNumber: string | null,
  reportId: ReportId,
  out: PlacedReportAction[],
): void {
  for (const item of thread?.work_items ?? []) {
    // F-FLAT (Ray 2026-11-03): the KIL bands each ACTION by its OWN
    // `association_tags` (not the Item's union) — a both-towers action shows under
    // each tower, a single-tower action only under its own (mirrors the .xlsx
    // re-key, report_558_xlsx._build_558_snapshot). Point is Item-level, echoed.
    for (const action of item.action_items ?? []) {
      const tags = action.report_tags ?? [];
      if (!tags.includes(reportId)) continue;
      if (DROPPED_ACTION_STATUS.has(action.status ?? "open")) continue;
      out.push({
        threadSlug: slug,
        threadName: name,
        threadDisplayNumber: displayNumber,
        itemSlug: item.slug,
        itemName: item.name,
        itemDisplayNumber: item.display_number ?? null,
        action,
        reportTags: tags,
        targetId: slug && action.ai_slug
          ? actionItemKey(slug, item.slug, action.ai_slug)
          : null,
        associations: action.association_tags ?? [],
        point: (item.point ?? "").trim(),
      });
    }
  }
}

/** Every OPEN action in the payload whose report_tags include `reportId`, in
 *  payload (display-number) order, grouped-ready by Category → Item. Walks both
 *  the named Category tree (`threads[]`) and the `unthreaded` bucket. */
export function collectPlacedActions(
  payload: BriefingPayload,
  reportId: ReportId,
): PlacedReportAction[] {
  const out: PlacedReportAction[] = [];
  for (const thread of payload.threads ?? []) {
    walkThreadActions(
      thread, thread.slug, thread.name ?? "Cross-cutting",
      thread.display_number ?? null, reportId, out,
    );
  }
  // `unthreaded` carries the same work_items[] shape (see BriefingPayload).
  const unthreaded = payload.unthreaded;
  if (unthreaded && Array.isArray(unthreaded.work_items)) {
    walkThreadActions(
      { work_items: unthreaded.work_items } as ThreadView,
      null, "Cross-cutting", null, reportId, out,
    );
  }
  return out;
}

// --- Recently-closed Key Issue Log rows (kil-marks snapshot) ---------------

/** One `key_issue_log.closed_actions` row from the agent's kil-marks snapshot
 *  (agent/portal.py::_kil_closed_action_rows). */
export interface KilClosedActionRow {
  thread_slug: string | null;
  thread_name: string;
  thread_display_number: string | null;
  item_slug: string;
  item_name: string;
  item_display_number: string | null;
  action: AnyItem;
}

export interface KilMarksWithKeyIssueLog {
  key_issue_log?: { closed_actions?: KilClosedActionRow[] };
  // ultrareview #495 #6: the project's configured associations in config order
  // with config display names (agent portal.py:2252) — the band axis the .xlsx
  // uses (report_558_xlsx.py:302 `band_name = {a.slug: a.name}`). Preferred over
  // projectAssociationSlugs()+humanizeSlug so the SPA preview matches the file.
  associations?: Array<{ slug: string; name: string }>;
}

/** Merge the snapshot's recently-closed 558 rows into the payload-derived
 *  placed list (dedup by Item + action; a payload row wins), then re-order by
 *  Category → Item → Action display number so a closed row sits where it was.
 *  Closed rows are display-only (no un-place handle). */
export function mergeClosedKilRows(
  placed: PlacedReportAction[],
  snapshot: KilMarksWithKeyIssueLog | null | undefined,
): PlacedReportAction[] {
  const rows = snapshot?.key_issue_log?.closed_actions ?? [];
  if (rows.length === 0) return placed;
  const seen = new Set(placed.map((p) => `${p.itemSlug}/${p.action.ai_slug ?? ""}`));
  const merged = [...placed];
  for (const r of rows) {
    const key = `${r.item_slug}/${r.action.ai_slug ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push({
      threadSlug: r.thread_slug,
      threadName: r.thread_name,
      threadDisplayNumber: r.thread_display_number,
      itemSlug: r.item_slug,
      itemName: r.item_name,
      itemDisplayNumber: r.item_display_number,
      action: { ...r.action, status: "closed" },
      reportTags: r.action.report_tags ?? [],
      targetId: null,
      // A merged-closed row bands by the closed action's own `association_tags`.
      associations: r.action.association_tags ?? [],
      point: "",
    });
  }
  return merged
    .map((p, i) => ({ p, i }))
    .sort((x, y) =>
      compareDisplayNumber(x.p.threadDisplayNumber, y.p.threadDisplayNumber)
      || compareDisplayNumber(x.p.itemDisplayNumber, y.p.itemDisplayNumber)
      || compareDisplayNumber(x.p.action.display_number, y.p.action.display_number)
      || x.i - y.i)
    .map(({ p }) => p);
}

/** Page subtitle. Closed rows (PSU 1-week parity) are counted separately — they
 *  show for the week but can't be removed here, so "remove one here" only
 *  describes the open ones. */
export function reportSubtitle(placed: PlacedReportAction[]): string {
  if (placed.length === 0) return "No actions placed in this report yet.";
  const closed = placed.filter((p) => p.action.status === "closed").length;
  const open = placed.length - closed;
  const n = (k: number) => `${k} action${k === 1 ? "" : "s"}`;
  const closedNote = closed > 0
    ? ` ${n(closed)} closed in the last week ${closed === 1 ? "stays" : "stay"} listed for the week.`
    : "";
  return `${n(open)} placed in this report.${closedNote} `
    + "Use the report toggle on an action in the briefing to add one; remove one here.";
}

// --- Key Issue Log grouping (Association band → FLAT action rows) ------------
// F-FLAT (Ray 2026-11-03 SPA-review call): the SPA-side mirror of the generated
// .xlsx's grouping (agent/report_558_xlsx.py::_build_558_snapshot), so the report
// page previews the structure a recipient gets on Generate. The sub-project,
// sub-category, Item-header, phase and numbered-Category tiers are ALL DROPPED;
// each OPEN 558 ACTION bands by its OWN association_tags (a both-tower action shows
// under each band); an action with no known association is EXCLUDED + counted (N2
// footer note), never an "Unassigned" section. A FLAT / no-axis project degrades to
// a single headerless action list.

export interface KilReportBand {
  /** null on the flat (no-axis) band. */
  slug: string | null;
  name: string;
  /** F-FLAT (Ray 2026-11-03): the band's actions, FLAT — no Item / sub-project /
   *  sub-category tiers — in action display-number order. A both-tower action
   *  appears in each of its bands (banded by the action's own association_tags). */
  actions: PlacedReportAction[];
}
export interface KilReportGrouping {
  bands: KilReportBand[];
  /** N2: a selected action with no known association (axis only). */
  excludedCount: number;
  /** true when the project has no association axis → one flat band, no header. */
  flat: boolean;
}

export function groupPlacedForReport(
  placed: PlacedReportAction[],
  associationOrder: Array<{ slug: string; name: string }>,
): KilReportGrouping {
  const byAction = (a: PlacedReportAction, b: PlacedReportAction) =>
    compareDisplayNumber(a.action.display_number, b.action.display_number);

  // Flat (no-axis) project: one headerless band of all placed actions, in action
  // display-number order (mirrors the .xlsx flat single file).
  if (associationOrder.length === 0) {
    return {
      flat: true,
      excludedCount: 0,
      bands: placed.length
        ? [{ slug: null, name: "", actions: [...placed].sort(byAction) }]
        : [],
    };
  }

  // F-FLAT: band each ACTION by its OWN associations (config order, known slugs
  // only). A both-tower action lands in each band; a selected action with no known
  // association is excluded + counted (N2). No Item / sub-project / sub-category tier.
  const known = new Set(associationOrder.map((a) => a.slug));
  const buckets = new Map<string, PlacedReportAction[]>();
  let excludedCount = 0;
  for (const p of placed) {
    const bandSlugs = associationOrder
      .map((a) => a.slug)
      .filter((s) => p.associations.includes(s) && known.has(s));
    if (bandSlugs.length === 0) { excludedCount += 1; continue; }
    for (const s of bandSlugs) buckets.set(s, [...(buckets.get(s) ?? []), p]);
  }
  const bands: KilReportBand[] = [];
  for (const a of associationOrder) {
    const acts = buckets.get(a.slug);
    if (!acts || acts.length === 0) continue;
    bands.push({ slug: a.slug, name: a.name, actions: [...acts].sort(byAction) });
  }
  return { bands, excludedCount, flat: false };
}

// --- Default filename -------------------------------------------------------
//   `YYYY.MMDD ProjectName-<Report>.<ext>` — mirrors the KIL default
//   (defaultGeneratedFilename). The Worker sets the ACTUAL extension from the
//   staged file's format on download; this is the recipient's editable seed.
const REPORT_FILENAME_SUFFIX: Record<ReportId, string> = {
  summary: "Summary.docx",
  "558": "558.xlsx",
};

export function defaultReportFilename(
  weekEnd: string | null,
  projectName: string,
  reportId: ReportId,
): string {
  const safeName = projectName.replace(/[^A-Za-z0-9_-]/g, "");
  const dateStr = weekEnd
    ? weekEnd.replace(/-/g, "").replace(/^(\d{4})(\d{2})(\d{2})$/, "$1.$2$3")
    : "draft";
  return `${dateStr} ${safeName}-${REPORT_FILENAME_SUFFIX[reportId]}`;
}

// --- Renderer ---------------------------------------------------------------

export interface RenderReportPageArgs {
  root: HTMLElement;
  reportId: ReportId;
  runId: string | null;
  projectName: string;
  weekEnd: string | null;
  placed: PlacedReportAction[];
  generations: ReportGenerationListItem[];
  /** P5c: the project's axes (drives the combined/split mode selector). */
  hasAssociationAxis?: boolean;
  hasSubProjectAxis?: boolean;
  /** F-FLAT: the ordered association axis (config order + names) that drives the
   *  Key Issue Log page's Association → flat action grouping. */
  associationOrder?: Array<{ slug: string; name: string }>;
  /** Re-fetch the generations archive after a Generate (mirrors the KIL/Hours
   *  rail refresh). */
  onGenerated: () => Promise<void>;
}

/** The generation modes to offer for a project, given its axes. A flat project
 *  gets none → the Generate form shows no mode selector (single-file, back-compat).
 *  With an axis, Combined is always offered; a split is offered only for an axis
 *  the project actually has (so the Worker mode filter never 404s no-matching). */
export function reportModesFor(
  hasAssociationAxis: boolean, hasSubProjectAxis: boolean,
): ReportMode[] {
  if (!hasAssociationAxis && !hasSubProjectAxis) return [];
  const modes: ReportMode[] = ["combined"];
  if (hasAssociationAxis) modes.push("split_association");
  if (hasSubProjectAxis) modes.push("split_subproject");
  return modes;
}

export function renderReportPage(args: RenderReportPageArgs): void {
  const { root, reportId, runId, projectName, weekEnd, placed, generations } = args;
  root.innerHTML = "";

  // Two-column layout mirroring the Hours-Log page: left rail (Generate +
  // archive) + main content (placed rows). Reuses the shared .layout/.left-rail
  // structure so the report pages feel like peers of the PSU/Hours pages.
  const layout = document.createElement("div");
  layout.className = "layout report-layout";

  const leftRail = document.createElement("div");
  leftRail.className = "left-rail";
  layout.appendChild(leftRail);

  const main = document.createElement("div");
  main.className = "viewer report-view";
  layout.appendChild(main);
  root.appendChild(layout);

  // --- Main: heading + subtitle + placed rows (shared by the gated + normal
  // Generate paths). ---
  const renderReportMain = (): void => {
    const heading = document.createElement("h2");
    heading.textContent = REPORT_HEADINGS[reportId];
    main.appendChild(heading);

    const subtitle = document.createElement("p");
    subtitle.className = "kil-marks-subtitle";
    subtitle.textContent = reportSubtitle(placed);
    main.appendChild(subtitle);

    if (placed.length === 0) {
      const empty = document.createElement("p");
      empty.className = "kil-marks-empty";
      empty.textContent =
        "No actions are placed in this report. Open the briefing, use the pencil on "
        + "an action, and choose “Include in client reports” to add it here.";
      main.appendChild(empty);
      return;
    }

    // Key Issue Log (558): F-FLAT (Ray 2026-11-03) — Association band → flat
    // action rows, mirroring the generated .xlsx (so the page previews the file's
    // structure). The Summary report (de-listed) keeps the Category layout.
    if (reportId === "558") {
      const grouping = groupPlacedForReport(placed, args.associationOrder ?? []);
      renderReportKilGrouping(main, grouping, reportId, runId);
      return;
    }

    // Summary (legacy): group by Category (thread) in payload order.
    const byThread = new Map<string, PlacedReportAction[]>();
    for (const p of placed) {
      const key = p.threadSlug ?? "_unthreaded";
      byThread.set(key, [...(byThread.get(key) ?? []), p]);
    }
    for (const group of byThread.values()) {
      main.appendChild(renderReportThreadSection(group, reportId, runId));
    }
  };

  // --- Left rail: Generate + archive ---
  const generateRow = document.createElement("div");
  generateRow.className = "kil-marks-generate";

  // Gate Generate on the backend renderer's existence (REPORT_HAS_RENDERER).
  // A report without a live renderer (e.g. the 558 until P5a-558) shows a
  // disabled button + a note instead of a dead-end that 409-loops — placement
  // review + the archive still work, so recipients can curate now.
  if (!REPORT_HAS_RENDERER[reportId]) {
    const disabledBtn = document.createElement("button");
    disabledBtn.type = "button";
    disabledBtn.className = "kil-generate-btn";
    disabledBtn.textContent = `Generate ${REPORT_LABELS[reportId]}`;
    disabledBtn.disabled = true;
    generateRow.appendChild(disabledBtn);
    const note = document.createElement("p");
    note.className = "kil-marks-subtitle report-generate-pending";
    note.textContent =
      "Document generation for this report is coming soon (P5a-558). You can "
      + "curate which Items are included now; the downloadable report will follow.";
    generateRow.appendChild(note);
    leftRail.appendChild(generateRow);
    if (generations.length > 0) {
      const genCard = document.createElement("aside");
      genCard.className = "sidebar kil-generations-sidebar";
      renderReportGenerationsSection(genCard, generations, reportId);
      leftRail.appendChild(genCard);
    }
    renderReportMain();
    return;
  }

  const generateBtn = document.createElement("button");
  generateBtn.type = "button";
  generateBtn.className = "kil-generate-btn";
  generateBtn.textContent = `Generate ${REPORT_LABELS[reportId]}`;
  // B (operator 2026-10-28): the split selector is retired — every report now
  // generates the single COMBINED file (the restyled multi-axis association →
  // sub-project layout). An axis project forces mode="combined" (the Worker serves
  // just the combined file, worker/src/schemas.ts); a flat project passes null (its
  // lone single file). `reportModesFor` stays for the Worker contract/tests but no
  // longer drives the UI — the form is given [] so no Format selector renders.
  const hasAxis = (args.hasAssociationAxis ?? false) || (args.hasSubProjectAxis ?? false);
  generateBtn.addEventListener("click", () => {
    const defaultFilename = defaultReportFilename(weekEnd, projectName, reportId);
    renderReportGenerateForm(generateRow, reportId, defaultFilename, [], async (filename, label, _mode) => {
      // Freshness is a Worker-side stored watermark (bumped on each placement),
      // not a client timestamp — so the SPA just submits; a stale staged blob
      // 409s + re-dispatches and the retry serves (mirrors the PSU/Hours flow).
      const outcome = await createReportGeneration({
        report_id: reportId, filename, label, mode: hasAxis ? "combined" : null,
      });
      if (outcome.status === "regenerating") {
        const note = document.createElement("p");
        note.className = "kil-marks-subtitle"; // muted/info, not error red
        note.textContent = outcome.detail;
        generateRow.appendChild(note);
        return;
      }
      // 201 — auto-download the first file through the authenticated fetch
      // layer (a bare anchor can't send X-Project-Slug post-#94). Decoupled
      // from the archive refresh: the generation is archived regardless of
      // whether the browser download starts.
      if (!inFixtureMode()) {
        try {
          await downloadFile(
            reportGenerationDownloadUrl(outcome.result.id, 0),
            outcome.result.filename,
          );
        } catch {
          // Generation succeeded; only the auto-download failed — non-fatal.
        }
      }
      await args.onGenerated();
    });
  });
  generateRow.appendChild(generateBtn);
  leftRail.appendChild(generateRow);

  if (generations.length > 0) {
    const genCard = document.createElement("aside");
    genCard.className = "sidebar kil-generations-sidebar";
    renderReportGenerationsSection(genCard, generations, reportId);
    leftRail.appendChild(genCard);
  }

  renderReportMain();
}

/** Render the Key Issue Log as Association band → FLAT action rows into `main`
 *  (F-FLAT, Ray 2026-11-03), mirroring the generated .xlsx. A flat (no-axis)
 *  grouping has one headerless band. Empty bands are already suppressed by
 *  `groupPlacedForReport` (built from the selected actions upward). */
function renderReportKilGrouping(
  main: HTMLElement,
  grouping: KilReportGrouping,
  reportId: ReportId,
  runId: string | null,
): void {
  for (const band of grouping.bands) {
    const bandSection = document.createElement("section");
    bandSection.className = "report-assoc-band";
    // Association band header (Title Case). The flat (no-axis) band has none.
    if (band.slug !== null) {
      const h = document.createElement("h2");
      h.className = "report-assoc-band-header";
      h.textContent = band.name;
      bandSection.appendChild(h);
    }
    // Flat action rows directly under the band — no Item / sub-project /
    // sub-category tiers. Each row carries its own un-place control; the band
    // section is the prune container (removeRowAndPrune drops it when emptied).
    const list = document.createElement("ul");
    list.className = "report-action-list";
    for (const pa of band.actions) {
      list.appendChild(renderReportActionRow(pa, reportId, runId, bandSection));
    }
    bandSection.appendChild(list);
    main.appendChild(bandSection);
  }
  // N2 footer: a selected action with no known association (axis only).
  if (grouping.excludedCount > 0) {
    const n = grouping.excludedCount;
    const note = document.createElement("p");
    note.className = "kil-marks-subtitle report-excluded-note";
    note.textContent =
      `* ${n} 558-tagged ${n === 1 ? "action" : "actions"} not yet assigned to an `
      + `association ${n === 1 ? "is" : "are"} excluded from this report.`;
    main.appendChild(note);
  }
}

function renderReportThreadSection(
  group: PlacedReportAction[],
  reportId: ReportId,
  runId: string | null,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "kil-thread";
  const header = document.createElement("h2");
  const first = group[0]!;
  if (first.threadDisplayNumber) {
    const num = document.createElement("span");
    num.className = "path-num";
    num.textContent = `${first.threadDisplayNumber}.`;
    header.appendChild(num);
  }
  header.appendChild(document.createTextNode(first.threadName));
  section.appendChild(header);
  // Within a Category, group the placed actions under their parent Item
  // (preserving payload order), mirroring the .xlsx: Item TITLE header + its
  // report-tagged action rows.
  const byItem = new Map<string, PlacedReportAction[]>();
  for (const p of group) {
    byItem.set(p.itemSlug, [...(byItem.get(p.itemSlug) ?? []), p]);
  }
  for (const itemActions of byItem.values()) {
    section.appendChild(renderReportItemCard(itemActions, reportId, runId));
  }
  return section;
}

// One Item card = the Item TITLE header (no Item narrative — parity with the KIL
// .xlsx, Ray line 530) + a row per report-tagged Action, each with its own
// remove control (un-place is action-tier: `action_set_report_tags`).
function renderReportItemCard(
  actions: PlacedReportAction[],
  reportId: ReportId,
  runId: string | null,
): HTMLElement {
  const first = actions[0]!;
  const card = document.createElement("article");
  card.className = "kil-item-card";
  card.dataset.slug = first.itemSlug;

  const header = document.createElement("header");
  header.className = "kil-item-header";
  const title = document.createElement("h4");
  const path = first.itemDisplayNumber ? `${first.itemDisplayNumber} ` : "";
  title.textContent = `${path}${first.itemName}`;
  header.appendChild(title);
  card.appendChild(header);
  // The 558 "Point" (responsible firm/s) is NOT on the Item header — it is
  // echoed onto each Action row's Point column, exactly as the .xlsx does
  // (report_558_xlsx _render_item: the Item title row has no Point; the Point is
  // written into each Action's column C). See renderReportActionRow.

  const list = document.createElement("ul");
  list.className = "report-action-list";
  for (const pa of actions) {
    list.appendChild(renderReportActionRow(pa, reportId, runId, card));
  }
  card.appendChild(list);
  return card;
}

/** Remove one action row, then prune the container it emptied — the flat KIL band
 *  (`.report-assoc-band`, where the band section is itself the `card`) or, on the
 *  Summary report, the Item card (`.kil-item-card`) + its Category (`.kil-thread`)
 *  once that holds no more cards. The optimistic removal's finalizer, gated behind
 *  the undo toast. (F-FLAT dropped the sub-project / sub-category tiers.) */
function removeRowAndPrune(row: HTMLElement, card: HTMLElement): void {
  const containers = [
    card.closest<HTMLElement>(".report-assoc-band"),
    card.closest<HTMLElement>(".kil-thread"),
  ].filter((x): x is HTMLElement => !!x);
  row.remove();
  if (!card.querySelector(".report-action-row")) {
    card.remove();
    for (const c of containers) {
      if (!c.querySelector(".kil-item-card")) c.remove();
    }
  }
}

function renderReportActionRow(
  placed: PlacedReportAction,
  reportId: ReportId,
  runId: string | null,
  card: HTMLElement,
): HTMLElement {
  const { action } = placed;
  const row = document.createElement("li");
  row.className = "report-action-row";
  if (action.ai_slug) row.dataset.aiSlug = action.ai_slug;

  const line = document.createElement("div");
  line.className = "report-action-line";
  if (action.display_number) {
    const num = document.createElement("span");
    num.className = "path-num report-action-num";
    num.textContent = action.display_number;
    line.appendChild(num);
  }
  const text = document.createElement("span");
  text.className = "report-action-text";
  text.textContent = action.text ?? action.what ?? "";
  line.appendChild(text);

  // Point (echoed from the parent Item onto each action row, mirroring the .xlsx
  // Point column). Rendered as a column cell so it aligns with Due down the list;
  // empty when the Item has no Point set.
  const pointCell = document.createElement("span");
  pointCell.className = "report-action-point";
  pointCell.textContent = placed.point || "";
  line.appendChild(pointCell);

  const due = document.createElement("span");
  due.className = "report-action-due";
  due.textContent = action.due ? fmtDue(action.due) : "TBD";
  line.appendChild(due);

  // The interactive un-place control — a placed action shows ✓ (parity with the
  // PSU mark button). Replace-whole: drop ONLY this report from the action's
  // current set, preserving any other placement. Suppressed (with a hint) when
  // there is no addressable target id (unthreaded / ai_slug-less action).
  if (action.status === "closed") {
    // PSU parity: a recently-closed row stays one week, badged, not toggleable.
    row.classList.add("kil-item-closed");
    const badge = document.createElement("span");
    badge.className = "kil-item-badge kil-item-badge-closed";
    const closedOn = String(action.closed_at ?? "").slice(0, 10);
    badge.textContent = closedOn ? `Closed · ${closedOn}` : "Closed";
    line.appendChild(badge);
    row.appendChild(line);
    if (action.narrative) {
      const p = document.createElement("p");
      p.className = "kil-item-narrative report-action-narrative";
      p.textContent = action.narrative;
      row.appendChild(p);
    }
    return row;
  }

  const removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.className = "kil-mark active report-mark";
  removeBtn.textContent = "✓";
  if (!placed.targetId) {
    removeBtn.disabled = true;
    removeBtn.title = "Remove this action in the briefing view (no direct handle here).";
    removeBtn.setAttribute("aria-label", removeBtn.title);
  } else {
    removeBtn.title = `Remove this action from the ${REPORT_LABELS[reportId]}`;
    removeBtn.setAttribute("aria-label", removeBtn.title);
    removeBtn.addEventListener("click", async () => {
      removeBtn.disabled = true;
      const newTags = computeUnplacedTags(placed.reportTags, reportId);
      try {
        if (!runId && !inFixtureMode()) {
          throw new Error("No briefing run to anchor the placement change against.");
        }
        await setActionReportTags(runId ?? "", placed.targetId!, newTags);
      } catch (e) {
        removeBtn.disabled = false;
        console.error("Report placement toggle failed:", e);
        let errEl = row.querySelector<HTMLElement>(".report-item-error");
        if (!errEl) {
          errEl = document.createElement("p");
          errEl.className = "report-item-error closed-item-error";
          row.appendChild(errEl);
        }
        errEl.textContent = "Couldn't update this placement — try again.";
        return;
      }
      // Optimistic: the briefing blob won't drop the tag until the next render
      // re-stamps it, so collapse the row from view now — but keep it in the DOM
      // so an Undo can restore it WITHOUT a confirm dialog. The undo toast
      // finalizes the removal (prune the row + emptied containers) on expiry, or
      // re-applies the original report_tags on Undo.
      row.classList.add("report-item-removed");
      showUndoToast({
        message: `Removed from the ${REPORT_LABELS[reportId]}`,
        onUndo: async () => {
          await setActionReportTags(runId ?? "", placed.targetId!, placed.reportTags);
          row.classList.remove("report-item-removed");
          removeBtn.disabled = false;
        },
        onExpire: () => removeRowAndPrune(row, card),
      });
    });
  }
  line.appendChild(removeBtn);
  row.appendChild(line);

  const narrative = action.narrative;
  if (narrative) {
    const p = document.createElement("p");
    p.className = "kil-item-narrative report-action-narrative";
    p.textContent = narrative;
    row.appendChild(p);
  }
  return row;
}

// An Action due → 'M/D/YYYY'; empty/garbage → 'TBD' (mirrors the .xlsx _fmt_due).
function fmtDue(iso: string): string {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "TBD";
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

// --- Generate form (report-scoped label; otherwise mirrors the KIL form) ----

function renderReportGenerateForm(
  container: HTMLElement,
  reportId: ReportId,
  defaultFilename: string,
  modes: ReportMode[],
  onSubmit: (
    filename: string, label: string | null, mode: ReportMode | null,
  ) => Promise<void>,
): void {
  container.innerHTML = "";
  const form = document.createElement("form");
  form.className = "kil-generate-form";

  const filenameLabel = document.createElement("label");
  filenameLabel.className = "kil-generate-field";
  const filenameLabelText = document.createElement("span");
  filenameLabelText.textContent = "Filename";
  const filenameInput = document.createElement("input");
  filenameInput.type = "text";
  filenameInput.value = defaultFilename;
  filenameInput.required = true;
  filenameInput.maxLength = 255;
  filenameLabel.appendChild(filenameLabelText);
  filenameLabel.appendChild(filenameInput);
  form.appendChild(filenameLabel);

  const labelLabel = document.createElement("label");
  labelLabel.className = "kil-generate-field";
  const labelLabelText = document.createElement("span");
  labelLabelText.textContent = "Label (optional)";
  const labelInput = document.createElement("input");
  labelInput.type = "text";
  labelInput.placeholder = "e.g. for Aug board mtg";
  labelInput.maxLength = 200;
  labelLabel.appendChild(labelLabelText);
  labelLabel.appendChild(labelInput);
  form.appendChild(labelLabel);

  // P5c: the combined/split mode selector, only for a project with an axis.
  let modeSelect: HTMLSelectElement | null = null;
  if (modes.length > 0) {
    const modeLabel = document.createElement("label");
    modeLabel.className = "kil-generate-field";
    const modeLabelText = document.createElement("span");
    modeLabelText.textContent = "Format";
    modeSelect = document.createElement("select");
    modeSelect.className = "report-mode-select";
    for (const m of modes) {
      const opt = document.createElement("option");
      opt.value = m;
      opt.textContent = REPORT_MODE_LABELS[m];
      modeSelect.appendChild(opt);
    }
    modeLabel.appendChild(modeLabelText);
    modeLabel.appendChild(modeSelect);
    form.appendChild(modeLabel);
  }

  const buttons = document.createElement("div");
  buttons.className = "kil-generate-form-buttons";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "kil-generate-btn";
  submit.textContent = "Submit and Download";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "kil-generate-cancel";
  cancel.textContent = "Cancel";
  cancel.addEventListener("click", () => {
    container.innerHTML = "";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "kil-generate-btn";
    btn.textContent = `Generate ${REPORT_LABELS[reportId]}`;
    btn.addEventListener("click", () => {
      renderReportGenerateForm(container, reportId, defaultFilename, modes, onSubmit);
    });
    container.appendChild(btn);
  });
  buttons.appendChild(submit);
  buttons.appendChild(cancel);
  form.appendChild(buttons);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    submit.textContent = "Creating…";
    cancel.disabled = true;
    const fname = filenameInput.value.trim();
    const label = labelInput.value.trim() || null;
    if (!fname) {
      submit.disabled = false;
      submit.textContent = "Submit and Download";
      cancel.disabled = false;
      filenameInput.focus();
      return;
    }
    const mode = modeSelect ? (modeSelect.value as ReportMode) : null;
    try {
      await onSubmit(fname, label, mode);
    } catch (err) {
      submit.disabled = false;
      submit.textContent = "Submit and Download";
      cancel.disabled = false;
      const errEl = document.createElement("p");
      errEl.className = "closed-item-error";
      errEl.textContent = `Generation failed: ${err instanceof Error ? err.message : String(err)}`;
      container.appendChild(errEl);
    }
  });

  container.appendChild(form);
  filenameInput.focus();
  filenameInput.select();
}

// --- Generations archive (files[] → one download link per file) -------------

export function renderReportGenerationsSection(
  parent: HTMLElement,
  generations: ReportGenerationListItem[],
  reportId: ReportId,
): void {
  // Only this report's generations (GET returns every report_id for the
  // project). Already newest-first from the Worker.
  const mine = generations.filter((g) => g.report_id === reportId);
  const header = document.createElement("h2");
  header.textContent = `Generated Reports (${mine.length})`;
  parent.appendChild(header);

  if (mine.length === 0) {
    const empty = document.createElement("p");
    empty.className = "kil-marks-subtitle";
    empty.textContent = "None yet.";
    parent.appendChild(empty);
    return;
  }

  const list = document.createElement("ul");
  list.className = "kil-generations-list";
  for (const gen of mine) {
    const li = document.createElement("li");
    li.className = "kil-generation-row";

    const tipParts: string[] = [];
    if (gen.label) tipParts.push(gen.label);
    tipParts.push(`Generated ${formatReportGeneratedAt(gen.generated_at)}`);
    tipParts.push(`by ${gen.generated_by}`);
    if (gen.week_end) tipParts.push(`week ending ${gen.week_end}`);

    const fname = document.createElement("span");
    fname.className = "kil-generation-filename";
    fname.textContent = gen.filename;
    fname.title = tipParts.join("\n");
    li.appendChild(fname);

    // One download control per file — the Summary has one; the 558 will have N
    // axis-split files. `?file=<idx>` selects which. Multi-file rows get an
    // axis-suffixed label so the links are distinguishable.
    gen.files.forEach((file, idx) => {
      const stem = gen.filename.replace(/\.(docx|xlsx|pdf)$/i, "");
      const axisSuffix = file.axis ? `-${file.axis}` : "";
      const fmtName = `${stem}${axisSuffix}.${file.format}`;
      const dl = document.createElement("a");
      dl.className = "kil-generation-download report-generation-download";
      dl.href = "#";
      dl.setAttribute("role", "button");
      const linkLabel = gen.files.length > 1 && file.axis
        ? `Download ${file.axis} (${file.format})`
        : `Download ${fmtName}`;
      dl.setAttribute("aria-label", linkLabel);
      dl.title = linkLabel;
      wireDownloadControl(dl, reportGenerationDownloadUrl(gen.id, idx), fmtName, (msg) => {
        let errEl = li.querySelector<HTMLElement>(".kil-generation-error");
        if (!errEl) {
          errEl = document.createElement("div");
          errEl.className = "kil-generation-error closed-item-error";
          li.appendChild(errEl);
        }
        errEl.textContent = msg;
      });
      // Multi-file: a small axis chip precedes the glyph so the links read.
      if (gen.files.length > 1 && file.axis) {
        const chip = document.createElement("span");
        chip.className = "report-generation-axis";
        chip.textContent = file.axis;
        dl.appendChild(chip);
      }
      dl.insertAdjacentHTML("beforeend",
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" '
        + 'stroke="currentColor" stroke-width="2" stroke-linecap="round" '
        + 'stroke-linejoin="round" aria-hidden="true">'
        + '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>'
        + '<polyline points="7 10 12 15 17 10"/>'
        + '<line x1="12" y1="15" x2="12" y2="3"/>'
        + "</svg>");
      li.appendChild(dl);
    });

    list.appendChild(li);
  }
  parent.appendChild(list);
}

export function formatReportGeneratedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

// --- View data load (used by app.ts renderReportView) -----------------------

export interface ReportViewData {
  runId: string | null;
  projectName: string;
  weekEnd: string | null;
  placed: PlacedReportAction[];
  generations: ReportGenerationListItem[];
  /** P5c: does this project carry an association / sub-project axis? Gates the
   *  combined-vs-split mode selector on the Generate form (a flat project keeps
   *  the single-file Generate). Derived from the payload the same way the lenses
   *  are — never from a config the SPA doesn't have. */
  hasAssociationAxis: boolean;
  hasSubProjectAxis: boolean;
  /** T1 report-parity: the ordered association axis (config order via
   *  `projectAssociationSlugs`, humanized names), which drives the Key Issue Log
   *  page's Association → flat action grouping. */
  associationOrder: Array<{ slug: string; name: string }>;
}

/** Load everything a report page needs: the latest briefing (for the placed
 *  rows + anchor run id) and this project's report generations. Fixture-aware
 *  fetchers are injected by the caller so preview mode serves static data. */
export async function loadReportViewData(
  reportId: ReportId,
  fetchers: {
    fetchIndex: () => Promise<Array<{ run_id: string; week_end: string }>>;
    fetchBriefingById: (runId: string) => Promise<{ payload: BriefingPayload }>;
    fetchGenerations: () => Promise<ReportGenerationsResponse>;
    /** Key Issue Log only: the kil-marks snapshot carrying recently-closed
     *  558 rows. Soft-fail — an error leaves the open rows as before. */
    fetchKilMarks?: () => Promise<KilMarksWithKeyIssueLog>;
  },
): Promise<ReportViewData> {
  const index = await fetchers.fetchIndex();
  const latest = index[0] ?? null;
  const wantClosed = reportId === "558" && fetchers.fetchKilMarks;
  const [briefing, gens, kil] = await Promise.all([
    latest ? fetchers.fetchBriefingById(latest.run_id) : Promise.resolve(null),
    fetchers.fetchGenerations(),
    wantClosed ? fetchers.fetchKilMarks!().catch(() => null) : Promise.resolve(null),
  ]);
  const payload = briefing?.payload;
  return {
    runId: latest?.run_id ?? null,
    projectName: payload?.project?.display_name ?? payload?.project?.name
      ?? payload?.project?.slug ?? "",
    weekEnd: payload?.week?.end ?? latest?.week_end ?? null,
    placed: payload
      ? mergeClosedKilRows(collectPlacedActions(payload, reportId), kil)
      : [],
    generations: gens.generations,
    hasAssociationAxis: payload
      ? availableLenses(payload).includes("association") : false,
    hasSubProjectAxis: (payload?.sub_projects?.length ?? 0) > 0,
    // #6 (ultrareview #495): prefer the config association axis from the kil-marks
    // snapshot (config order + display names — what the .xlsx bands by). Fall back
    // to the payload-derived, humanized axis for a flat/pre-T1 project with no
    // snapshot associations.
    associationOrder: (kil?.associations?.length)
      ? kil.associations
      : payload
        ? projectAssociationSlugs(payload).map((slug) => ({ slug, name: humanizeSlug(slug) }))
        : [],
  };
}

// Non-fixture default fetchers (thin wrappers so app.ts can pass either these
// or the fixture equivalents into loadReportViewData).
export const liveReportFetchers = {
  fetchIndex: () => fetchBriefingIndex(),
  fetchBriefingById: (runId: string) => fetchBriefing(runId),
  fetchGenerations: () => fetchReportGenerations(),
};
